import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get } from "../../lib/http";
import { API } from "../../config/api";
import { issueTicketPath, offlineBookingsPath, resolveLockedEventId } from "../../organizer/eventContext.js";
import { formatDateTimeIST } from "../../utils/datetime.js";

const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function OrganizerCashBookingsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const lockedEventId = resolveLockedEventId({
    pathname: location.pathname,
    state: location.state,
    searchParams,
    params,
  });

  const [events, setEvents] = useState([]);
  const [lockedEvent, setLockedEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [eventId, setEventId] = useState(lockedEventId || searchParams.get("eventId") || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState("-createdAt");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lockedEventId && eventId !== lockedEventId) {
      setEventId(lockedEventId);
      setPage(1);
    }
  }, [lockedEventId, eventId]);

  useEffect(() => {
    if (!lockedEventId) {
      setLockedEvent(null);
      return undefined;
    }
    let on = true;
    (async () => {
      try {
        const data = await get(`${API.events.getSinglEvent}/${lockedEventId}`);
        if (!on) return;
        setLockedEvent(data?.event || data || null);
      } catch {
        if (on) setLockedEvent(null);
      }
    })();
    return () => {
      on = false;
    };
  }, [lockedEventId]);

  useEffect(() => {
    if (lockedEventId) return undefined;
    let on = true;
    (async () => {
      try {
        const data = await get(`${API.events.mySubmissions}?approvalStatus=approved&limit=100`);
        if (on) setEvents(data?.events || []);
      } catch {
        if (on) setEvents([]);
      }
    })();
    return () => {
      on = false;
    };
  }, [lockedEventId]);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set("paginate", "true");
    p.set("page", String(page));
    p.set("limit", String(limit));
    if (sort) p.set("sort", sort);
    if (eventId) p.set("eventId", eventId);
    if (dateFrom) p.set("from", dateFrom);
    if (dateTo) p.set("to", dateTo);
    if (q) p.set("q", q);
    return p.toString();
  }, [page, limit, sort, eventId, dateFrom, dateTo, q]);

  useEffect(() => {
    let on = true;
    (async () => {
      setLoading(true);
      try {
        const resp = await get(`${API.Payment.listCashBooking}?${queryParams}`);
        if (!on) return;
        setRows(resp?.data || []);
        setTotal(resp?.total || 0);
      } catch {
        if (!on) return;
        setRows([]);
        setTotal(0);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [queryParams]);

  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const lockedEventName = lockedEvent?.name || lockedEvent?.title || "";

  if (lockedEventId && searchParams.get("eventId") !== lockedEventId) {
    return <Navigate to={offlineBookingsPath(lockedEventId)} replace state={{ eventId: lockedEventId }} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase text-amber-700">Issue tickets</div>
          <h1 className="text-xl font-extrabold text-slate-900 sm:text-2xl">Offline bookings</h1>
          <p className="mt-0.5 text-sm text-slate-600">
            {lockedEventName
              ? `Offline tickets you issued for ${lockedEventName}.`
              : lockedEventId
                ? "Offline tickets you issued for this event."
                : "Offline tickets you issued for your events."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {lockedEventId ? (
            <Link
              to={`/events/dashboard/${lockedEventId}`}
              className="inline-flex rounded-full border px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              ← Dashboard
            </Link>
          ) : null}
          <Link
            to={issueTicketPath(eventId || lockedEventId)}
            state={eventId || lockedEventId ? { eventId: eventId || lockedEventId } : undefined}
            className="inline-flex rounded-full bg-amber-400 px-4 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300"
          >
            Issue ticket
          </Link>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-100 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-5">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600">Search</label>
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Name / email / mobile / booking code"
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">Event</label>
            {lockedEventId ? (
              <div className="mt-1 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-slate-900">
                {lockedEventName || "Loading event…"}
              </div>
            ) : (
              <select
                value={eventId}
                onChange={(e) => {
                  setEventId(e.target.value);
                  setPage(1);
                }}
                className="mt-1 w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
              >
                <option value="">All my events</option>
                {events.map((ev) => (
                  <option key={ev._id || ev.id} value={ev._id || ev.id}>
                    {ev.name || ev.title}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button
            onClick={() => setSort(sort === "-createdAt" ? "createdAt" : "-createdAt")}
            className="rounded-full border px-3 py-1.5 text-xs hover:bg-slate-50"
          >
            {sort === "-createdAt" ? "Newest first" : "Oldest first"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-amber-100 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b bg-amber-50/70 text-slate-700">
              <th className="px-4 py-2 text-left">Booking</th>
              <th className="px-4 py-2 text-left">Event</th>
              <th className="px-4 py-2 text-left">Guest</th>
              <th className="px-4 py-2 text-left">Issued by</th>
              <th className="px-4 py-2 text-left">Tickets</th>
              <th className="px-4 py-2 text-left">Amount</th>
              <th className="px-4 py-2 text-left">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                  No offline bookings yet
                </td>
              </tr>
            )}
            {rows.map((b) => {
              const totalTickets = (b?.ticketCategories || []).reduce(
                (s, c) => s + Number(c.finalQuantity || 0),
                0
              );
              const assg = b?.assignedBy || b?.assignee || null;
              const amount = Number(b?.finalAmount || 0);
              return (
                <tr
                  key={b._id}
                  className="cursor-pointer border-t hover:bg-amber-50/40"
                  onClick={() => navigate(`/listyourshow/cash-bookings/${b._id}`)}
                >
                  <td className="px-4 py-2">
                    <div className="font-semibold text-slate-900">{b.bookingCode || b._id}</div>
                    <div className="text-xs text-slate-500">offline</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{b?.eventId?.name || "-"}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{b?.buyer?.name || b?.userId?.name || "-"}</div>
                    <div className="text-xs text-slate-500">
                      {b?.buyer?.email || b?.userId?.email || b?.buyer?.mobile || b?.userId?.mobile || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{assg?.name || "-"}</div>
                  </td>
                  <td className="px-4 py-2">{totalTickets}</td>
                  <td className="px-4 py-2">{amount === 0 ? "Free" : fmtInr(amount)}</td>
                  <td className="px-4 py-2 text-slate-700">
                    {b?.createdAt ? formatDateTimeIST(b.createdAt) : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t px-4 py-3">
          <div className="text-xs text-slate-600">
            Page {page} / {totalPages} • {total} results
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-lg border px-2 py-1 text-xs"
            >
              {[10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={`rounded-lg border px-3 py-1.5 text-xs ${page <= 1 ? "cursor-not-allowed opacity-50" : "hover:bg-slate-50"}`}
            >
              Prev
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className={`rounded-lg border px-3 py-1.5 text-xs ${page >= totalPages ? "cursor-not-allowed opacity-50" : "hover:bg-slate-50"}`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
